import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePro } from "@/contexts/ProContext";
import { toast } from "sonner";

export const ProBanner = () => {
  const { isPro, bannerDismissed, activatePro, dismissBanner } = usePro();
  const { t } = useTranslation();

  if (isPro || bannerDismissed) return null;

  const activate = async () => {
    try {
      await activatePro();
      toast.success(t("pro.activated"));
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden border-b border-[#3a2a00]"
        style={{ background: "linear-gradient(90deg, rgba(255,215,0,0.15) 0%, rgba(255,165,0,0.1) 100%)" }}
        data-testid="pro-banner"
      >
        <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent, rgba(255,215,0,0.08), transparent)", backgroundSize: "200% 100%", animation: "shimmer 6s linear infinite" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles className="h-4 w-4 shrink-0" style={{ color: "#FFD700" }} strokeWidth={1.5} />
            <span className="text-sm truncate" style={{ color: "#FFD700" }} data-testid="pro-banner-text">
              ✨ {t("pro.banner")}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={activate}
              className="px-3 h-8 rounded-md text-xs font-semibold text-black transition hover:opacity-90 active:scale-95"
              style={{ background: "linear-gradient(135deg, #FFD700, #FFA500)" }}
              data-testid="pro-banner-activate"
            >
              {t("pro.activate")}
            </button>
            <button
              onClick={dismissBanner}
              className="h-8 w-8 rounded-md flex items-center justify-center hover:bg-foreground/10 transition"
              aria-label={t("pro.dismiss")}
              data-testid="pro-banner-dismiss"
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export const ProBadge = ({ className = "" }) => {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-2 h-5 rounded-full text-[10px] font-bold tracking-wider text-black ${className}`}
      style={{ background: "linear-gradient(135deg, #FFD700, #FFA500)" }}
      data-testid="pro-badge"
    >
      <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
      {t("pro.badge")}
    </span>
  );
};
